import {Button, Fab, MenuItem, useMediaQuery} from '@material-ui/core';
import {Add} from '@material-ui/icons';
import {ModalState, showModal} from 'Core/ducks/modal';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {Async, fromResult, isLoaded, toPage} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useRouteMatch} from 'react-router-dom';
import {bindActionCreators} from 'redux';
import {ReducerState} from 'rootReducer';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import withUserClaims from 'Shared/components/withUserClaims';
import {ActionsOf} from 'Shared/utilities/actionCreator';
import {SSOAccount} from 'Users/api/apiTypes';
import 'Users/components/SingleSignOn/SingleSignOnAccounts.sass';
import * as SSOAccountActions from 'Users/ducks/SSO/ssoAccount';
import {getAccounts} from 'Users/ducks/SSO/ssoAccounts';
import {UserClaims} from 'Users/models/Claims';
import {SSOEntityId} from 'Users/models/SSOEntityId';
import {UserRouteConstants} from 'Users/Routes';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';
import {changeAnalyticsState} from 'Core/ducks/analytics';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';

type SingleSignOnAccountProps = {
  claims: UserClaims;
} & WithSelectedAgencyProps;

const SingleSignOnAccounts = (props: SingleSignOnAccountProps) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  useBreadcrumbs('Single Sign-On Accounts', match.url);
  const {selectedAgency} = props;
  const {accounts} = useSelector((state: ReducerState) => state.user.sso);

  const {featureVariables} = useSelector((state: ReducerState) => state.featureToggle);

  const {sso, deleteAccount} = bindActionCreators(SSOAccountActions as ActionsOf<typeof SSOAccountActions>, dispatch);
  const onDelete = (ssoAccount: SSOAccount) => () =>
    deleteAccount(selectedAgency.encryptedIdentifier, props.claims.id, ssoAccount.username!);
  const onEMWSSO = (ssoAccount: SSOAccount) => () => {
    sso(selectedAgency.encryptedIdentifier, props.claims.id, ssoAccount.username!, SSOEntityId.EMW);
    dispatch(showModal(ModalState.SSOConfirmation({message: SSOEntityId.EMW})));
  };
  const onEasyBillSSO = (ssoAccount: SSOAccount) => () => {
    sso(selectedAgency.encryptedIdentifier, props.claims.id, ssoAccount.username!, SSOEntityId.EasyBill);
    dispatch(showModal(ModalState.SSOConfirmation({message: SSOEntityId.EasyBill})));
  };
  useEffect(() => {
    dispatch(getAccounts(selectedAgency.encryptedIdentifier, props.claims.id));
  }, []);

  useEffect(() => {
    dispatch(updatefeatureVariables({...featureVariables, FOOTER_OPEN_DEFAULT: false}));
  }, []);

  const accountsPage = isLoaded(accounts) ? fromResult(toPage(accounts.Data)) : accounts;
  const AddButton = (
    <Link to={UserRouteConstants.ADD_SSO_ACCOUNT}>
      {isDesktop ? (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            dispatch(
              changeAnalyticsState({
                page: analytics.page,
                ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.SINGLE_SIGNON_HEADER},
              })
            );
          }}
        >
          <Add /> Add Account
        </Button>
      ) : (
        <Fab className="sso-accounts__add-account-button" color="primary">
          <i className="fa fa-plus" />
        </Fab>
      )}
    </Link>
  );

  return (
    <>
      <PagedView
        pagedViewKey="singleSignOnAccounts"
        loadMore={() => {}}
        data={accountsPage as Async<Page<SSOAccount>>}
        actions={AddButton}
        eventAction="singleSignOnHeader_settingClick"
      >
        {columnProps => (
          <>
            <Column {...columnProps} title="Username" default ddprivacy="mask">
              {ssoAccount => ssoAccount.username}
            </Column>
            <Column {...columnProps} title="Is Legacy Account" default>
              {ssoAccount => (!ssoAccount.isPrimary ? 'Yes' : 'No')}
            </Column>
            <Column {...columnProps} title="" default isActions>
              {ssoAccount => (
                <>
                  <Button className="sso-accounts__emw-button" color="primary" onClick={onEMWSSO(ssoAccount)}>
                    EMW
                  </Button>
                  <Button className="sso-accounts__emw-button" color="primary" onClick={onEasyBillSSO(ssoAccount)}>
                    EasyBill
                  </Button>
                  <ActionsMenu>
                    <>
                      <Link to={UserRouteConstants.EDIT_SSO_ACCOUNT(ssoAccount.username!)}>
                        <MenuItem>Edit</MenuItem>
                      </Link>
                      <MenuItem style={{color: 'red'}} onClick={onDelete(ssoAccount)}>
                        Remove
                      </MenuItem>
                    </>
                  </ActionsMenu>
                </>
              )}
            </Column>
          </>
        )}
      </PagedView>
    </>
  );
};

export default withSelectedAgency(withUserClaims(SingleSignOnAccounts));
